/**
 * SDK developer docs — dash data layer (feature FS-228).
 *
 * Thin typed wrapper around `apiClient` for the `/api/v1/sdk-docs`
 * surface. Mirrors the backend contract in
 * `apps/api/src/routes/sdk-docs.ts`:
 *
 *   GET    /sdks                 → { sdks: SdkDocEntryView[] }
 *   POST   /sdks                 → { sdk: SdkDocEntryView }
 *   PATCH  /sdks/:id             → { sdk: SdkDocEntryView }
 *   DELETE /sdks/:id             → { sdk: SdkDocEntryView, deleted: true }
 *   GET    /endpoints            → { endpoints: EndpointCatalogEntryView[] }
 *   POST   /endpoints            → { endpoint: EndpointCatalogEntryView }
 *   PATCH  /endpoints/:id        → { endpoint: EndpointCatalogEntryView }
 *   DELETE /endpoints/:id        → { endpoint: EndpointCatalogEntryView, deleted: true }
 *   GET    /search?q=<term>      → { sdks: [], endpoints: [] }
 *
 * Pure helpers (validators, client-side schemas, search match, filter +
 * sort, markdown renderer) live here too so unit tests can drive them
 * directly without DOM. Validators mirror the server regex.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types — mirror server Views
// ---------------------------------------------------------------------------

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export const HTTP_METHODS: readonly HttpMethod[] = [
	"GET",
	"POST",
	"PATCH",
	"PUT",
	"DELETE",
];

export type CatalogScope = "global" | "tenant";

export interface SdkDocEntryView {
	id: string;
	tenant_id: string | null;
	name: string;
	platform: string;
	version: string;
	changelog_url: string | null;
	docs_url: string | null;
	supported_languages: string[];
	quickstart_md: string;
	active: boolean;
	scope: CatalogScope;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

export interface EndpointCatalogEntryView {
	id: string;
	tenant_id: string | null;
	method: HttpMethod;
	path: string;
	summary: string;
	request_schema: string;
	response_schema: string;
	scopes: string[];
	active: boolean;
	scope: CatalogScope;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

// ---------------------------------------------------------------------------
// Validators — mirror server regex
// ---------------------------------------------------------------------------

const PLATFORM_RE = /^[a-z][a-z0-9+-]{1,31}$/;
const SEMVER_RE =
	/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;
export const API_V1_PREFIX = "/api/v1/";
export const MAX_QUICKSTART = 64 * 1024;

export function isSemVer(raw: string): boolean {
	if (typeof raw !== "string") return false;
	if (raw.length === 0 || raw.length > 128) return false;
	return SEMVER_RE.test(raw);
}

export function normalizePlatform(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const v = raw.trim().toLowerCase();
	return PLATFORM_RE.test(v) ? v : null;
}

export function isHttpMethod(raw: string): raw is HttpMethod {
	return typeof raw === "string" && (HTTP_METHODS as readonly string[]).includes(raw);
}

export function isApiV1Path(raw: string): boolean {
	if (typeof raw !== "string") return false;
	if (raw.length === 0 || raw.length > 256) return false;
	if (!raw.startsWith(API_V1_PREFIX)) return false;
	for (let i = 0; i < raw.length; i++) {
		const c = raw.charCodeAt(i);
		if (c < 0x21 || c === 0x22 || c === 0x5c) return false;
	}
	return true;
}

export function parseList(input: string): string[] {
	return input
		.split(/[\s,]+/)
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
}

// ---------------------------------------------------------------------------
// Drafts + schemas
// ---------------------------------------------------------------------------

export interface SdkDraft {
	name: string;
	platform: string;
	version: string;
	changelogUrl: string;
	docsUrl: string;
	languagesText: string;
	quickstart: string;
}

export const EMPTY_SDK_DRAFT: SdkDraft = {
	name: "",
	platform: "",
	version: "",
	changelogUrl: "",
	docsUrl: "",
	languagesText: "",
	quickstart: "",
};

export interface ValidatedSdkDraft {
	name: string;
	platform: string;
	version: string;
	changelog_url: string | null;
	docs_url: string | null;
	supported_languages: string[];
	quickstart_md: string;
}

export interface EndpointDraft {
	method: string;
	path: string;
	summary: string;
	requestSchema: string;
	responseSchema: string;
	scopesText: string;
}

export const EMPTY_ENDPOINT_DRAFT: EndpointDraft = {
	method: "GET",
	path: "",
	summary: "",
	requestSchema: "",
	responseSchema: "",
	scopesText: "",
};

export interface ValidatedEndpointDraft {
	method: HttpMethod;
	path: string;
	summary: string;
	request_schema: string;
	response_schema: string;
	scopes: string[];
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

function validateOptionalUrl(raw: string): string | null | "invalid" {
	const trimmed = raw.trim();
	if (trimmed.length === 0) return null;
	if (trimmed.length > 512) return "invalid";
	if (!/^https?:\/\//.test(trimmed)) return "invalid";
	return trimmed;
}

export const sdkDraftSchema = {
	safeParse(input: SdkDraft): ParseResult<ValidatedSdkDraft> {
		const errors: Record<string, string> = {};

		const name = input.name?.trim() ?? "";
		if (name.length === 0) {
			errors.name = "Name is required.";
		} else if (name.length > 120) {
			errors.name = "Name must be 120 characters or fewer.";
		}

		const platform = normalizePlatform(input.platform ?? "");
		if (!platform) {
			errors.platform =
				"Platform is a lowercase kebab-style id (e.g. ios, react-native).";
		}

		const version = (input.version ?? "").trim();
		if (!isSemVer(version)) {
			errors.version = "Version must be valid SemVer (e.g. 1.2.3).";
		}

		const changelog = validateOptionalUrl(input.changelogUrl ?? "");
		const docs = validateOptionalUrl(input.docsUrl ?? "");
		if (changelog === "invalid") {
			errors.changelogUrl = "Must be an http(s) URL under 512 characters.";
		}
		if (docs === "invalid") {
			errors.docsUrl = "Must be an http(s) URL under 512 characters.";
		}

		const languages = parseList(input.languagesText ?? "");
		if (languages.length > 16) {
			errors.languagesText = "Up to 16 languages.";
		}

		const quickstart = input.quickstart ?? "";
		if (quickstart.length > MAX_QUICKSTART) {
			errors.quickstart = "Quickstart markdown exceeds 64 KiB.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				name,
				platform: platform as string,
				version,
				changelog_url: changelog as string | null,
				docs_url: docs as string | null,
				supported_languages: languages,
				quickstart_md: quickstart,
			},
		};
	},
};

export const endpointDraftSchema = {
	safeParse(input: EndpointDraft): ParseResult<ValidatedEndpointDraft> {
		const errors: Record<string, string> = {};

		const method = (input.method ?? "").trim().toUpperCase();
		if (!isHttpMethod(method)) {
			errors.method = `Method must be one of ${HTTP_METHODS.join(", ")}.`;
		}

		const path = (input.path ?? "").trim();
		if (!isApiV1Path(path)) {
			errors.path = "Path must start with /api/v1/ and contain no whitespace.";
		}

		const summary = (input.summary ?? "").trim();
		if (summary.length > 512) {
			errors.summary = "Summary must be 512 characters or fewer.";
		}

		const scopes = parseList(input.scopesText ?? "");
		if (scopes.length > 16) {
			errors.scopesText = "Up to 16 scopes.";
		}

		const requestSchema = input.requestSchema ?? "";
		const responseSchema = input.responseSchema ?? "";
		if (requestSchema.length > 64 * 1024) {
			errors.requestSchema = "Request schema exceeds 64 KiB.";
		}
		if (responseSchema.length > 64 * 1024) {
			errors.responseSchema = "Response schema exceeds 64 KiB.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				method: method as HttpMethod,
				path,
				summary,
				request_schema: requestSchema,
				response_schema: responseSchema,
				scopes,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function methodBadgeClass(method: HttpMethod): string {
	switch (method) {
		case "GET":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "POST":
			return "bg-sky-50 text-sky-700 border-sky-200";
		case "PATCH":
			return "bg-amber-50 text-amber-700 border-amber-200";
		case "PUT":
			return "bg-violet-50 text-violet-700 border-violet-200";
		case "DELETE":
			return "bg-red-50 text-red-700 border-red-200";
	}
}

export function scopeBadgeClass(scope: CatalogScope): string {
	if (scope === "tenant") {
		return "bg-emerald-50 text-emerald-700 border-emerald-200";
	}
	return "bg-zinc-100 text-zinc-700 border-zinc-200";
}

export function formatTimestamp(seconds: number | null): string {
	if (seconds === null || Number.isNaN(seconds)) return "-";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "-";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

export function sortSdks(
	sdks: readonly SdkDocEntryView[],
): SdkDocEntryView[] {
	return sdks.slice().sort((a, b) => {
		if (a.platform !== b.platform) return a.platform.localeCompare(b.platform);
		return b.version.localeCompare(a.version);
	});
}

export function sortEndpoints(
	endpoints: readonly EndpointCatalogEntryView[],
): EndpointCatalogEntryView[] {
	return endpoints.slice().sort((a, b) => {
		if (a.path !== b.path) return a.path.localeCompare(b.path);
		return a.method.localeCompare(b.method);
	});
}

export function filterEndpointsByMethod(
	endpoints: readonly EndpointCatalogEntryView[],
	methods: readonly HttpMethod[],
): EndpointCatalogEntryView[] {
	if (methods.length === 0) return endpoints.slice();
	const set = new Set<HttpMethod>(methods);
	return endpoints.filter((e) => set.has(e.method));
}

export function searchEndpointsLocal(
	endpoints: readonly EndpointCatalogEntryView[],
	query: string,
): EndpointCatalogEntryView[] {
	const q = query.trim().toLowerCase();
	if (q.length === 0) return endpoints.slice();
	return endpoints.filter(
		(e) =>
			e.path.toLowerCase().includes(q) ||
			e.method.toLowerCase().includes(q) ||
			e.summary.toLowerCase().includes(q),
	);
}

export function searchSdksLocal(
	sdks: readonly SdkDocEntryView[],
	query: string,
): SdkDocEntryView[] {
	const q = query.trim().toLowerCase();
	if (q.length === 0) return sdks.slice();
	return sdks.filter(
		(s) =>
			s.name.toLowerCase().includes(q) ||
			s.platform.toLowerCase().includes(q) ||
			s.version.toLowerCase().includes(q),
	);
}

export function countByMethod(
	endpoints: readonly EndpointCatalogEntryView[],
): Record<HttpMethod, number> {
	const out: Record<HttpMethod, number> = {
		GET: 0,
		POST: 0,
		PATCH: 0,
		PUT: 0,
		DELETE: 0,
	};
	for (const e of endpoints) out[e.method] += 1;
	return out;
}

export function replaceSdkInList(
	list: readonly SdkDocEntryView[],
	next: SdkDocEntryView,
): SdkDocEntryView[] {
	let hit = false;
	const out = list.map((r) => {
		if (r.id === next.id) {
			hit = true;
			return next;
		}
		return r;
	});
	if (!hit) out.push(next);
	return out;
}

export function removeSdkFromList(
	list: readonly SdkDocEntryView[],
	id: string,
): SdkDocEntryView[] {
	return list.filter((r) => r.id !== id);
}

export function replaceEndpointInList(
	list: readonly EndpointCatalogEntryView[],
	next: EndpointCatalogEntryView,
): EndpointCatalogEntryView[] {
	let hit = false;
	const out = list.map((r) => {
		if (r.id === next.id) {
			hit = true;
			return next;
		}
		return r;
	});
	if (!hit) out.push(next);
	return out;
}

export function removeEndpointFromList(
	list: readonly EndpointCatalogEntryView[],
	id: string,
): EndpointCatalogEntryView[] {
	return list.filter((r) => r.id !== id);
}

/**
 * Render a markdown quickstart to a minimal sanitized HTML string.
 * Supports headings, bold, italics, inline code, code blocks, and
 * newlines — enough for an SDK "install + copy snippet" intro. No
 * external deps so the test can round-trip without a DOM.
 */
export function renderMarkdown(md: string): string {
	if (!md) return "";
	const escaped = md
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");

	const lines = escaped.split(/\r?\n/);
	const out: string[] = [];
	let inCode = false;
	let codeBuf: string[] = [];
	for (const line of lines) {
		if (line.startsWith("```")) {
			if (inCode) {
				out.push(`<pre><code>${codeBuf.join("\n")}</code></pre>`);
				codeBuf = [];
				inCode = false;
			} else {
				inCode = true;
			}
			continue;
		}
		if (inCode) {
			codeBuf.push(line);
			continue;
		}
		if (line.startsWith("### ")) {
			out.push(`<h3>${line.slice(4)}</h3>`);
			continue;
		}
		if (line.startsWith("## ")) {
			out.push(`<h2>${line.slice(3)}</h2>`);
			continue;
		}
		if (line.startsWith("# ")) {
			out.push(`<h1>${line.slice(2)}</h1>`);
			continue;
		}
		if (line.trim().length === 0) {
			out.push("");
			continue;
		}
		let html = line;
		html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
		html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
		html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
		out.push(`<p>${html}</p>`);
	}
	if (inCode && codeBuf.length > 0) {
		out.push(`<pre><code>${codeBuf.join("\n")}</code></pre>`);
	}
	return out.join("\n");
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/sdk-docs";

interface SdkListResponse {
	sdks: SdkDocEntryView[];
}
interface SdkResponse {
	sdk: SdkDocEntryView;
}
interface EndpointListResponse {
	endpoints: EndpointCatalogEntryView[];
}
interface EndpointResponse {
	endpoint: EndpointCatalogEntryView;
}
interface SearchResponse {
	sdks: SdkDocEntryView[];
	endpoints: EndpointCatalogEntryView[];
}

export async function listSdks(): Promise<SdkDocEntryView[]> {
	try {
		const res = await apiClient.get<SdkListResponse>(`${BASE}/sdks`);
		return res.sdks ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function createSdk(
	payload: ValidatedSdkDraft,
): Promise<SdkDocEntryView> {
	const res = await apiClient.post<SdkResponse>(`${BASE}/sdks`, payload);
	return res.sdk;
}

export interface SdkPatchPayload {
	name?: string;
	version?: string;
	changelog_url?: string | null;
	docs_url?: string | null;
	supported_languages?: string[];
	quickstart_md?: string;
	active?: boolean;
}

export async function patchSdk(
	id: string,
	patch: SdkPatchPayload,
): Promise<SdkDocEntryView> {
	const res = await apiClient.patch<SdkResponse>(
		`${BASE}/sdks/${encodeURIComponent(id)}`,
		patch,
	);
	return res.sdk;
}

export async function deleteSdk(id: string): Promise<SdkDocEntryView> {
	const res = await apiClient.delete<SdkResponse & { deleted: true }>(
		`${BASE}/sdks/${encodeURIComponent(id)}`,
	);
	return res.sdk;
}

export async function listEndpoints(): Promise<EndpointCatalogEntryView[]> {
	try {
		const res = await apiClient.get<EndpointListResponse>(`${BASE}/endpoints`);
		return res.endpoints ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function createEndpoint(
	payload: ValidatedEndpointDraft,
): Promise<EndpointCatalogEntryView> {
	const res = await apiClient.post<EndpointResponse>(
		`${BASE}/endpoints`,
		payload,
	);
	return res.endpoint;
}

export interface EndpointPatchPayload {
	summary?: string;
	request_schema?: string;
	response_schema?: string;
	scopes?: string[];
	method?: HttpMethod;
	path?: string;
	active?: boolean;
}

export async function patchEndpoint(
	id: string,
	patch: EndpointPatchPayload,
): Promise<EndpointCatalogEntryView> {
	const res = await apiClient.patch<EndpointResponse>(
		`${BASE}/endpoints/${encodeURIComponent(id)}`,
		patch,
	);
	return res.endpoint;
}

export async function deleteEndpoint(
	id: string,
): Promise<EndpointCatalogEntryView> {
	const res = await apiClient.delete<EndpointResponse & { deleted: true }>(
		`${BASE}/endpoints/${encodeURIComponent(id)}`,
	);
	return res.endpoint;
}

export async function searchCatalog(query: string): Promise<SearchResponse> {
	const res = await apiClient.get<SearchResponse>(`${BASE}/search`, {
		query: { q: query },
	});
	return { sdks: res.sdks ?? [], endpoints: res.endpoints ?? [] };
}

// ---------------------------------------------------------------------------
// Query key bag — kept for compatibility with react-query later.
// ---------------------------------------------------------------------------

export const sdkDocsQueryKeys = {
	all: ["sdk-docs"] as const,
	sdkList: ["sdk-docs", "sdks"] as const,
	endpointList: ["sdk-docs", "endpoints"] as const,
	search: (q: string) => ["sdk-docs", "search", q] as const,
};

export const DEVELOPERS_BASE_URL = "https://developers.trackerity.com";
